// storeService.ts
import apiService, { API_URL } from "../apiUtils"
import axiosInstance from "../../CustomAxios/axiosInstance"

// Define the Store interface based on your database schema
export interface Store {
  id?: number
  store_name: string
  slogan: string
  email: string
  phone: string
  address: string
  logo: string | null
  primary_color: string
  secondary_color: string
  vat_rate: number
  loyalty_point_value: number
  card_deduction: number
  admin_password?: string
  have_table: number // Changed back to number for consistency
  ice: string
}

// Define the StoreHeader interface that matches the API response
export interface StoreHeader {
    store_name: string
    slogan: string
    email: string
    phone: string
    address: string
    logo: string | null
    primary_color: string
    secondary_color: string
    loyalty_point_value: number
    admin_password: string
    card_deduction: number
    vat_rate?: number
    have_table?: number // Keep as number
    ice?: string
}

// Default store data to use as fallback
const defaultStoreData: Store = {
    store_name: "Default Store",
    slogan: "Your slogan here",
    email: "store@example.com",
    phone: "0600000000",
    address: "Store Address",
    logo: null,
    primary_color: "#10b981",
    secondary_color: "#ffffff",
    vat_rate: 20,
    loyalty_point_value: 0.5,
    card_deduction: 0,
    have_table: 0, // Number
    ice: ""
}

// Default store header data
const defaultStoreHeader: StoreHeader = {
    store_name: "Default Store",
    slogan: "Your slogan here",
    email: "store@example.com",
    phone: "0600000000",
    address: "Store Address",
    logo: null,
    primary_color: "#10b981",
    secondary_color: "#ffffff",
    loyalty_point_value: 0.5,
    admin_password: "",
    card_deduction: 0,
    vat_rate: 20,
    have_table: 0, // Number
    ice: ""
}

// In-memory cache
let storeDataCache: Store | null = null
let storeHeaderCache: StoreHeader | null = null
let vatRateCache: number | null = null

// Cache timestamps and duration
let storeHeaderCacheTime = 0
let storeDataCacheTime = 0
let vatRateCacheTime = 0
const CACHE_DURATION = 5 * 60 * 1000 // 5 minutes in milliseconds

const storeService = {
    /**
     * Clear the cache to force fresh data fetch
     */
    clearCache(): void {
        storeDataCache = null
        storeHeaderCache = null
        vatRateCache = null
        storeHeaderCacheTime = 0
        storeDataCacheTime = 0
        vatRateCacheTime = 0
        
        if (apiService.clearCache) {
            apiService.clearCache()
        }
    },

    /**
     * Get VAT rate from the API
     */
    async getVatRate(): Promise<number> {
        try {
            const now = Date.now()
            if (vatRateCache !== null && now - vatRateCacheTime < CACHE_DURATION) {
                                return vatRateCache
            }

            const response = await apiService.get("/store/params")

            if (response.status === "success" && response.data) {
                const vatRate = Number.parseFloat(response.data.vat_rate) || defaultStoreData.vat_rate

                vatRateCache = vatRate
                vatRateCacheTime = now

                return vatRate
            }

            return defaultStoreData.vat_rate
        } catch (error) {
                        return defaultStoreData.vat_rate
        }
    },

    /**
     * Get store data from the API - fetches the single store row
     */
    async getStoreData(): Promise<Store> {
        try {
            const now = Date.now()
            if (storeDataCache && now - storeDataCacheTime < CACHE_DURATION) {
                                return storeDataCache
            }

            try {
                const response = await apiService.get("/store/params")

                if (response.status === "success" && response.data) {
                    const storeData = response.data
                                        
                    const transformedData: Store = {
                        id: storeData.id,
                        store_name: storeData.store_name || "",
                        slogan: storeData.slogan || "",
                        email: storeData.email || "",
                        phone: storeData.phone || "",
                        address: storeData.address || "",
                        logo: storeData.logo ? `${API_URL}/storage/${storeData.logo}` : null,
                        primary_color: storeData.primary_color || "#10b981",
                        secondary_color: storeData.secondary_color || "#ffffff",
                        vat_rate: Number.parseFloat(storeData.vat_rate) || defaultStoreData.vat_rate,
                        loyalty_point_value: Number.parseFloat(storeData.loyalty_point_value) || 0,
                        card_deduction: Number.parseFloat(storeData.card_deduction || "0") || 0,
                        admin_password: storeData.admin_password,
                        have_table: Number.parseInt(storeData.have_table) || 0, // Ensure it's a number
                        ice: storeData.ice || ""
                    }

                    storeDataCache = transformedData
                    storeDataCacheTime = now
                    vatRateCache = transformedData.vat_rate
                    vatRateCacheTime = now

                                        return transformedData
                }
            } catch (paramsError) {
                            }

            try {
                const headerData = await this.getStoreHeader()
                const vatRate = headerData.vat_rate !== undefined ? headerData.vat_rate : await this.getVatRate()

                const transformedData: Store = {
                    store_name: headerData.store_name,
                    slogan: headerData.slogan,
                    email: headerData.email,
                    phone: headerData.phone,
                    address: headerData.address,
                    logo: headerData.logo,
                    primary_color: headerData.primary_color,
                    secondary_color: headerData.secondary_color,
                    vat_rate: vatRate,
                    loyalty_point_value: headerData.loyalty_point_value,
                    card_deduction: headerData.card_deduction,
                    admin_password: headerData.admin_password,
                    have_table: headerData.have_table || 0, // Ensure it's a number
                    ice: headerData.ice || ""
                }

                storeDataCache = transformedData
                storeDataCacheTime = now

                                return transformedData
            } catch (headerError) {
                            }

            throw new Error("Failed to load store data from any endpoint")
        } catch (error) {
                                    return defaultStoreData
        }
    },

    /**
     * Get store header data - comprehensive store information
     */
    async getStoreHeader(): Promise<StoreHeader> {
        try {
            const now = Date.now()
            if (storeHeaderCache && now - storeHeaderCacheTime < CACHE_DURATION) {
                                return storeHeaderCache
            }

            const response = await apiService.get("/store/store_header")

            if (response.status === "success" && response.data) {
                const headerData = response.data
                
                const transformedHeader: StoreHeader = {
                    store_name: headerData.store_name || defaultStoreHeader.store_name,
                    slogan: headerData.slogan || defaultStoreHeader.slogan,
                    email: headerData.email || defaultStoreHeader.email,
                    phone: headerData.phone || defaultStoreHeader.phone,
                    address: headerData.address || defaultStoreHeader.address,
                    logo: headerData.logo ? `${API_URL}/storage/${headerData.logo}` : null,
                    primary_color: headerData.primary_color || defaultStoreHeader.primary_color,
                    secondary_color: headerData.secondary_color || defaultStoreHeader.secondary_color,
                    loyalty_point_value: Number.parseFloat(headerData.loyalty_point_value) || defaultStoreHeader.loyalty_point_value,
                    admin_password: headerData.admin_password || defaultStoreHeader.admin_password,
                    card_deduction: Number.parseFloat(headerData.card_deduction) || defaultStoreHeader.card_deduction,
                    ...(headerData.vat_rate !== undefined && { vat_rate: Number.parseFloat(headerData.vat_rate) }),
                    ...(headerData.have_table !== undefined && { have_table: Number.parseInt(headerData.have_table) }),
                    ...(headerData.ice !== undefined && { ice: headerData.ice }),
                }

                storeHeaderCache = transformedHeader
                storeHeaderCacheTime = now

                return transformedHeader
            }

            throw new Error("Failed to load store header data")
        } catch (error) {
                                    return defaultStoreHeader
        }
    },

    /**
     * Update store data - updates the single store row
     */
    async updateStoreData(storeData: FormData): Promise<any> {
        try {
            console.log("Updating store with data (keys only):", Array.from(storeData.keys()))

            const response = await apiService.post("/store/params", storeData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                    Accept: "application/json",
                },
            })

            if (response.status === "success") {
                this.clearCache()
                return response
            }

            throw new Error(response.message || "Failed to update store data")
        } catch (error) {
                        throw error
        }
    },

    /**
     * Get admin password
     */
    async getAdminPassword(): Promise<string | null> {
        try {
            const headerData = await this.getStoreHeader()
            return headerData.admin_password || null
        } catch (error) {
                        return null
        }
    },

    /**
     * Get card deduction rate percentage
     */
    async getCardDeduction(): Promise<number> {
        try {
            const headerData = await this.getStoreHeader()
            return headerData.card_deduction / 100
        } catch (error) {
                        return 0.025
        }
    },

    /**
     * Verify if a provided password matches the admin password
     */
    async verifyPassword(password: string): Promise<any> {
        const adminPassword = await axiosInstance.post("/store/verify-admin-password",{admin_password: password})
    },
}

export default storeService